/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo } from 'react';

import type { TrackingData } from 'js/lib/retracked';

import { VisuallyHidden, typography2 } from '@coursera/cds-core';

import TrackedButton from 'bundles/page/components/TrackedButton';

export type ChipButtonProps = {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  id?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  children?: React.ReactNode;
  isActive?: boolean;
  isDisabled?: boolean;
  isFocusableWhenDisabled?: boolean;
  showLabel?: boolean;
  count?: number;
  showCount?: boolean;
  height?: string;
  trackingName?: string;
  trackingData?: TrackingData;
  isNlSuggestion?: boolean;
  testId?: string;
  disableSideMargin?: boolean;
};

const styles = {
  chipButton: css`
    ${typography2.actionSecondary}
    align-items: center;
    display: flex;
    border-radius: 20px;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    margin: 0 3px;
    height: 36px;
    gap: 4px;
    font-size: 14px;
    border: 1px solid var(--cds-color-grey-100);
    background-color: var(--cds-color-neutral-background-primary);
    outline-offset: 3px;
    outline-width: 1px;

    &:hover {
      background-color: var(--cds-color-neutral-background-primary-weak);
    }

    &:active {
      background-color: var(--cds-color-neutral-background-primary-weak);
      border-color: var(--cds-color-grey-100);
    }

    .prefix,
    .suffix {
      height: var(--cds-spacing-200);

      svg {
        height: var(--cds-spacing-200);
        width: var(--cds-spacing-200);
      }
    }

    &:disabled,
    &[aria-disabled='true'] {
      background-color: var(--cds-color-neutral-background-primary);
      border: 1px solid var(--cds-color-neutral-disabled-strong);
      color: var(--cds-color-neutral-disabled-strong);

      &:hover,
      &:focus {
        background-color: var(--cds-color-neutral-background-primary);
        border: 1px solid var(--cds-color-neutral-disabled-strong);
        color: var(--cds-color-neutral-disabled-strong);
      }
    }
  `,

  nlSuggestion: css`
    color: var(--cds-color-neutral-primary);
    font-weight: 400;
    padding-left: 8px;
    padding-right: 10px;
  `,
  noSideMargin: css`
    margin: 0;
  `,

  circleButton: css`
    .prefix {
      margin-left: -5px;
      margin-right: -5px;
    }
  `,

  isSelected: css`
    background: var(--cds-color-neutral-background-primary-weak);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);

    &:hover,
    &:focus {
      background: var(--cds-color-neutral-background-primary-weak);
    }

    &:disabled,
    &[aria-disabled='true'] {
      background-color: var(--cds-color-neutral-disabled-strong);
      border: 1px solid var(--cds-color-neutral-disabled-strong);
      color: var(--cds-color-neutral-primary-invert);

      &:hover,
      &:focus {
        background-color: var(--cds-color-neutral-disabled-strong);
        border: 1px solid var(--cds-color-neutral-disabled-strong);
        color: var(--cds-color-neutral-primary-invert);
      }
    }
  `,
  chipLabel: css`
    white-space: nowrap;
  `,
};

export enum CHIP_BUTTON_STATES {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
  DISABLED_ACTIVE = 'DISABLED_ACTIVE',
  INACTIVE = 'INACTIVE',
}

const CHIP_BUTTON_STATE_CONFIGS = {
  [`${CHIP_BUTTON_STATES.ACTIVE}`]: {
    testId: 'chip-button-active',
  },
  [`${CHIP_BUTTON_STATES.DISABLED}`]: {
    testId: 'chip-button-disabled',
  },
  [`${CHIP_BUTTON_STATES.DISABLED_ACTIVE}`]: {
    testId: 'chip-button-disabled-active',
  },
  [`${CHIP_BUTTON_STATES.INACTIVE}`]: {
    testId: 'chip-button-inactive',
  },
} as const;

export const getInitialState = (isActive?: boolean, isDisabled?: boolean) => {
  if (isActive && !isDisabled) {
    return CHIP_BUTTON_STATES.ACTIVE;
  } else if (!isActive && isDisabled) {
    return CHIP_BUTTON_STATES.DISABLED;
  } else if (isActive && isDisabled) {
    return CHIP_BUTTON_STATES.DISABLED_ACTIVE;
  } else {
    return CHIP_BUTTON_STATES.INACTIVE;
  }
};

export const ChipButton = memo(
  ({
    onClick,
    id,
    prefix,
    isActive,
    isDisabled,
    isFocusableWhenDisabled = false,
    suffix,
    showLabel = true,
    showCount = true,
    count,
    children,
    height,
    trackingName,
    trackingData,
    isNlSuggestion,
    testId,
    disableSideMargin = false,
  }: ChipButtonProps) => {
    const chipButtonState = getInitialState(isActive, isDisabled);
    const isAriaDisabled = Boolean(isDisabled && isFocusableWhenDisabled);
    const wrappedChildren = children ? <div css={styles.chipLabel}>{children}</div> : null;

    const buttonContent = (
      <>
        {prefix && <div className="prefix">{prefix}</div>}
        {wrappedChildren && (showLabel ? wrappedChildren : <VisuallyHidden>{wrappedChildren}</VisuallyHidden>)}
        {count !== undefined && showCount && <span>({count})</span>}
        {suffix && <div className="suffix">{suffix}</div>}
      </>
    );

    const buttonProps = {
      css: [
        styles.chipButton,
        isNlSuggestion && styles.nlSuggestion,
        disableSideMargin && styles.noSideMargin,
        isActive && styles.isSelected,
        !showLabel && !showCount && styles.circleButton,
      ],
      style: height ? { height } : undefined,
      'data-testid': testId ?? CHIP_BUTTON_STATE_CONFIGS[chipButtonState].testId,
      id,
      disabled: isDisabled && !isFocusableWhenDisabled,
      'aria-disabled': isAriaDisabled || undefined,
      onClickCapture: isAriaDisabled
        ? (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
          }
        : undefined,
    };

    if (trackingName) {
      return (
        <TrackedButton
          withVisibilityTracking
          onClick={onClick}
          trackingName={trackingName}
          data={trackingData}
          type="button"
          {...buttonProps}
        >
          {buttonContent}
        </TrackedButton>
      );
    }

    return (
      <button onClick={onClick} type="button" {...buttonProps}>
        {buttonContent}
      </button>
    );
  }
);

export default ChipButton;
